import { useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { Alert } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

interface AppDownload {
  fileName: string;
  version: string;
  sizeBytes: number;
  uploadedAt: string;
}

const URL_INFO = '/media/downloads/keora-multimedia';
/** Partes de 8 MB: pasan holgadas por el límite de 25 MB del servidor. */
const PART = 8 * 1024 * 1024;
const mb = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

/**
 * La app KEORA Multimedia para Windows: un renglón pequeño al final de Multimedia, solo en la
 * computadora (en el celular no sirve y se oculta). La administración de KEORA sube cada versión desde esta misma tarjeta.
 */
export function AppDownloadCard() {
  const me = useMe();
  const info = useAsync((signal) => api.get<{ download: AppDownload | null }>(URL_INFO, signal).then((r) => r.download), []);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState('');

  async function upload(file: File) {
    setError(null);
    const parts = Math.ceil(file.size / PART);
    try {
      for (let i = 0; i < parts; i++) {
        setProgress(Math.round((i / parts) * 100));
        const chunk = new File([file.slice(i * PART, (i + 1) * PART)], file.name, { type: 'application/octet-stream' });
        await api.putFile(`${URL_INFO}/parts/${i}`, chunk);
      }
      const guessed = version.trim() || /(\d+\.\d+\.\d+)/.exec(file.name)?.[1] || '1.0.0';
      await api.post(`${URL_INFO}/complete`, { parts, fileName: file.name, version: guessed, sizeBytes: file.size });
      setProgress(null);
      info.reload();
    } catch (e) {
      setProgress(null);
      setError(errorMessage(e));
    }
  }

  const d = info.status === 'ready' ? info.data : null;
  if (!d && !me.isPlatformAdmin) return null;

  return (
    <aside className="app-download" aria-label="KEORA Multimedia para Windows">
      {d ? (
        <p className="small muted">
          ¿Proyectas desde Windows?{' '}
          <a href={`/api${URL_INFO}/file`} download={d.fileName} title="Cada pantalla sale sola a su monitor y los videos pueden quedarse en esa computadora. Si Windows dice «Windows protegió su PC», toca «Más información» y luego «Ejecutar de todas formas».">
            Descarga la app
          </a>{' '}
          (versión {d.version} · {mb(d.sizeBytes)})
        </p>
      ) : null}
      {me.isPlatformAdmin ? (
        <details className="app-download__admin">
          <summary className="small">{d ? 'Subir una versión nueva (administración de KEORA)' : 'Subir el instalador (administración de KEORA)'}</summary>
          {error ? <Alert>{error}</Alert> : null}
          <div className="form">
            <div className="field">
              <label htmlFor="app-version">Versión (opcional)</label>
              <input id="app-version" value={version} maxLength={30} placeholder="Se toma del nombre del archivo" onChange={(e) => setVersion(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="app-file">Instalador (.exe)</label>
              <input id="app-file" type="file" accept=".exe" disabled={progress !== null} onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} />
            </div>
            {progress !== null ? (
              <p className="small" role="status">
                Subiendo… {progress}%
              </p>
            ) : null}
          </div>
        </details>
      ) : null}
    </aside>
  );
}
